import { KitType } from '@repo/types';
import { Button, Card, MicrobesOutlineBg, Modal, Typography } from '@repo/ui';
import useAcknowledgment from 'hooks/acknowledgments/useAcknowledgment';
import useAgreement from 'hooks/agreements/useAgreement';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useState } from 'react';
import useCreateAcknowledgment from 'services/acknowledgments/useCreateAcknowledgment';
import useImpersonate from 'services/auth/useImpersonate';
import type { SingleConditionData } from 'services/conditions/useConditionMetrics';
import { AcknowledgmentResource } from 'types/Acknowledgment';
import { AgreementKey } from 'types/Agreement';
import conditionSubject from 'utils/conditions/conditionSubject';
import formatConditionName from 'utils/conditions/formatConditionName';
import useGate2Tracking from './hooks/useGate2Tracking';
import styles from './SensitiveConditionsReminderModal.module.css';

type ModalProps = {
  condition: SingleConditionData;
};

export default function SensitiveConditionsReminderModal({
  condition,
}: ModalProps) {
  const conditionId = `${condition.condition_id}`;
  const { currentKit, currentKitId, currentTinyAccountId, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const [isOpen, setIsOpen] = useState(true); // Only impersonator can close it

  const acknowledgment = useAcknowledgment(
    AcknowledgmentResource.CONDITIONS_METRICS,
    conditionId,
  );

  const { mutate, isPending } = useCreateAcknowledgment();

  const { impersonateId } = useImpersonate();

  // the educational modal runs off this agreement and has to be finished first;
  // an impersonator never writes one, so they get both at once
  const nonDiagnostic = useAgreement(AgreementKey.NON_DIAGNOSTIC);

  const open =
    (!!nonDiagnostic || !!impersonateId) && !acknowledgment && isOpen;

  const { markDismissed } = useGate2Tracking({
    open,
    acknowledged: !!acknowledgment,
    condition,
  });

  function handleSubmit() {
    if (!!impersonateId || !!acknowledgment) {
      markDismissed();
      return setIsOpen(false);
    }

    mutate({
      resource_id: conditionId,
      resource_type: AcknowledgmentResource.CONDITIONS_METRICS,
      tinyaccount_id: currentTinyAccountId ?? '',
      kit_id: currentKitId ?? '',
    });
  }

  const isGut = currentKit?.sampling_type === KitType.STOOL;

  const formattedConditionName = formatConditionName(
    condition.condition_display_name,
  );

  return (
    <Modal isOpen={open} className={styles.modalContainer}>
      <MicrobesOutlineBg />
      <div className={styles.modal}>
        <Typography
          variant='heading-l'
          className={styles.title}
          color='cold-grey-900'
        >
          {`Some reminders before you explore your associations to ${formattedConditionName}:`}
        </Typography>
        <div className={styles.cards}>
          <Card>
            <Typography variant='body-s' weight='medium'>
              This is not a diagnosis of {formattedConditionName} –
            </Typography>
            <Typography variant='body-s'>
              Microbiome associations do not mean{' '}
              {conditionSubject(
                currentKit?.age_at_sampling,
                currentTinyAccount?.first_name,
              )}{' '}
              or will develop {formattedConditionName}.
            </Typography>
          </Card>
          <Card>
            Research shows that {formattedConditionName} is linked to{' '}
            {isGut ? 'gut' : 'vaginal'} microbiome differences, but it is still
            unknown if one causes the other.
          </Card>
        </div>
        <Button
          label='Got it'
          color='purple-300'
          width='full'
          onClick={handleSubmit}
          disabled={!!acknowledgment || isPending}
          loading={isPending}
        />
      </div>
    </Modal>
  );
}
